const DEBUG_ENDPOINT = "/api/app/v2/debug/frontend-logs";
const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE_SIZE = 500;

type DebugEntry = {
  timestamp: string;
  level: "debug";
  event: string;
  data: Record<string, unknown>;
};

let enabled = false;
const queue: DebugEntry[] = [];
let originalFetch: typeof window.fetch | undefined;

export function isRuntimeDevelopment(): boolean {
  return enabled;
}

export async function installDevelopmentRequestLogging(): Promise<void> {
  if (typeof window === "undefined" || originalFetch) return;
  originalFetch = window.fetch.bind(window);
  await enableFromRuntimeConfig();
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const request = describeRequest(input, init);
    const startedAt = performance.now();
    try {
      const response = await originalFetch!(input, init);
      void describeResponse(response).then((result) => {
        record("frontend_fetch_request", { request, result, durationMs: roundedDuration(startedAt) });
      });
      return response;
    } catch (error) {
      record("frontend_fetch_request", { request, error: describeError(error), durationMs: roundedDuration(startedAt) });
      throw error;
    }
  };
  if (!enabled) return;
  window.setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
  window.addEventListener("pagehide", () => { void flush(); });
}

export function debugFrontendEvent(event: string, data: Record<string, unknown> = {}): void {
  record("frontend_flow_event", { event, ...data });
}

export function debugFrontendXhrRequest(input: {
  method: string;
  url: string;
  body?: unknown;
  result?: unknown;
  status?: number;
  error?: unknown;
  durationMs: number;
}): void {
  record("frontend_xhr_request", input);
}

function record(event: string, data: Record<string, unknown>): void {
  if (!enabled) return;
  queue.push({ timestamp: new Date().toISOString(), level: "debug", event, data: sanitize(data) as Record<string, unknown> });
  if (queue.length > MAX_QUEUE_SIZE) queue.splice(0, queue.length - MAX_QUEUE_SIZE);
}

async function flush(): Promise<void> {
  if (!enabled || queue.length === 0 || !originalFetch) return;
  const entries = queue.splice(0, 10);
  try {
    const response = await originalFetch(DEBUG_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json", "x-ppt-development-debug": "1" },
      body: JSON.stringify({ entries }),
      keepalive: true,
    });
    if (!response.ok) throw new Error(`debug log endpoint returned ${response.status}`);
  } catch {
    queue.unshift(...entries);
    if (queue.length > MAX_QUEUE_SIZE) queue.splice(0, queue.length - MAX_QUEUE_SIZE);
  }
}

async function enableFromRuntimeConfig(): Promise<void> {
  if (!originalFetch) return;
  const request = { method: "GET", url: "/api/app/config" };
  const startedAt = performance.now();
  try {
    const response = await originalFetch("/api/app/config");
    const result = await describeResponse(response);
    const body = result.body as { developmentDebugLogging?: { enabled?: unknown } } | undefined;
    if (body?.developmentDebugLogging?.enabled !== true) return;
    enabled = true;
    record("frontend_fetch_request", { request, result, durationMs: roundedDuration(startedAt) });
  } catch {
    // Development logging must never block application startup.
  }
}

function describeRequest(input: RequestInfo | URL, init?: RequestInit): Record<string, unknown> {
  const source = input instanceof Request ? input : undefined;
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  return { method: init?.method || source?.method || "GET", url, body: describeBody(init?.body) };
}

async function describeResponse(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get("content-type") || "";
  const result: Record<string, unknown> = {
    status: response.status,
    ok: response.ok,
    contentType,
    requestId: response.headers.get("x-request-id") || undefined,
  };
  if (!/application\/(?:json|problem\+json)|^text\//i.test(contentType)) return result;
  try { result.body = parseText(await response.clone().text()); }
  catch (error) { result.bodyReadError = describeError(error); }
  return result;
}

function describeBody(body: BodyInit | null | undefined): unknown {
  if (body === null || body === undefined) return undefined;
  if (typeof body === "string") return parseText(body);
  if (body instanceof URLSearchParams) return Object.fromEntries(body.entries());
  if (body instanceof FormData) return Array.from(body.entries()).map(([key, value]) => ({
    key,
    value: value instanceof File ? { fileName: value.name, size: value.size, type: value.type } : value,
  }));
  if (body instanceof Blob) return { size: body.size, type: body.type };
  return String(body);
}

function parseText(value: string): unknown {
  const truncated = value.length > 8_000 ? `${value.slice(0, 8_000)}...[TRUNCATED]` : value;
  try { return JSON.parse(truncated); } catch { return truncated; }
}

function describeError(error: unknown): Record<string, string> {
  return error instanceof Error ? { name: error.name, message: error.message } : { name: "Error", message: String(error) };
}

function roundedDuration(startedAt: number): number {
  return Math.round((performance.now() - startedAt) * 10) / 10;
}

function sanitize(value: unknown, key = "", depth = 0, seen = new WeakSet<object>()): unknown {
  if (isSecretKey(key)) return "[REDACTED]";
  if (isSensitiveContentKey(key)) return contentSummary(value);
  if (value === null || value === undefined || typeof value === "boolean" || typeof value === "number") return value;
  if (typeof value === "string") return value.length <= 4_000 ? value : `${value.slice(0, 4_000)}...[TRUNCATED]`;
  if (Array.isArray(value)) return value.slice(0, 50).map((item) => sanitize(item, "", depth + 1, seen));
  if (typeof value !== "object" || depth >= 5) return depth >= 5 ? "[MAX_DEPTH]" : String(value);
  if (seen.has(value)) return "[CIRCULAR]";
  seen.add(value);
  return Object.fromEntries(Object.entries(value).map(([childKey, childValue]) => [childKey, sanitize(childValue, childKey, depth + 1, seen)]));
}

function isSecretKey(key: string): boolean {
  return /(?:authorization|cookie|password|token|secret|api[_-]?key|captcha|verificationCode|smsCode)/i.test(key);
}

function isSensitiveContentKey(key: string): boolean {
  return /^(?:content|contentText|text|prompt|instruction|sourceFacts|sourceExcerpts|ocrText|document|dataUrl|imageDataUrl|bytes|imageData|stylePrompt|finalPrompt|negativePrompt|.*Instruction|.*Text)$/i.test(key);
}

function contentSummary(value: unknown): string {
  if (typeof value === "string") return `[REDACTED_CONTENT chars=${value.length}]`;
  if (value instanceof Blob) return `[REDACTED_CONTENT bytes=${value.size} type=${value.type || "unknown"}]`;
  try {
    const serialized = JSON.stringify(value) || "";
    return `[REDACTED_CONTENT chars=${serialized.length}]`;
  } catch {
    return "[REDACTED_CONTENT]";
  }
}
